import React from 'react'
import { Box, Typography } from '@mui/material'
import { PatientHistoryType } from 'src/graphql.generated'
import { PatientHistoryEntry } from '../../api/useAdvancedPatientHistory'
import { DefaultBody, TypedFields, valueOf } from './EntryBody'
import { isNutritionIntakeNote } from './eventLogger/advancedNoteKinds'
import { LayoutBody, NUTRITION_INTAKE_LAYOUT, layoutForType } from './historyLayouts'
import { HistoryFieldRow } from './HistoryValueTable'
import { SUM_SCORED_QUESTIONS } from './sumScoredQuestionnaires'

const renderDescription = (entry: PatientHistoryEntry) => (
  <Typography variant="body2">{(valueOf(entry)?.description as string | undefined) || '—'}</Typography>
)

// Stored keys aren't the question wording, and jsonb doesn't keep their order.
const renderSumScoredQuestionnaire = (entry: PatientHistoryEntry) => {
  const questions = entry.type && SUM_SCORED_QUESTIONS[entry.type]
  const value = valueOf(entry)

  if (!questions) {
    return <DefaultBody entry={entry} />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {questions.map(({ key, label }) => (
        <HistoryFieldRow key={key} label={label} value={value?.[key]} />
      ))}
    </Box>
  )
}

const BESPOKE_BODY: Partial<Record<PatientHistoryType, (entry: PatientHistoryEntry) => React.ReactNode>> = {
  [PatientHistoryType.Note]: renderDescription,
  [PatientHistoryType.PhoneCall]: renderDescription,
  [PatientHistoryType.Sms]: renderDescription,
  [PatientHistoryType.AnonymousNote]: renderDescription,
  [PatientHistoryType.LabOrderNote]: renderDescription,
  [PatientHistoryType.Phq9]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Gad7]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Who5]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Asrm]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Isi]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Ocir]: renderSumScoredQuestionnaire,
  [PatientHistoryType.Pcl5]: renderSumScoredQuestionnaire,
}

export const renderBodyForType = (entry: PatientHistoryEntry): React.ReactNode => {
  const value = valueOf(entry)
  const layout = isNutritionIntakeNote(value) ? NUTRITION_INTAKE_LAYOUT : layoutForType(entry.type)

  if (layout) {
    return (
      <>
        <TypedFields entry={entry} />
        <LayoutBody sections={layout} source={value} />
      </>
    )
  }

  if (entry.type === PatientHistoryType.VideoCall) {
    return <TypedFields entry={entry} />
  }

  const bespoke = entry.type && BESPOKE_BODY[entry.type]

  return bespoke ? bespoke(entry) : <DefaultBody entry={entry} />
}
